import { useTranslation } from 'react-i18next'
import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Link } from '@tanstack/react-router'
import { Phone } from 'lucide-react'

export const Route = createFileRoute('/$lang/faq')({
    staticData: {
        title: 'FAQ — PhoneSalad',
        description: 'Frequently asked questions about PhoneSalad — how it works, call quality, billing, supported countries, and everything you need to get started.',
        sitemap: { priority: '0.6', changefreq: 'monthly' },
    },
    component: FAQPage,
})

interface FAQItem {
    id: string
    category: string
    question: string
    answer: string
    featured: boolean
}

function FAQPage() {
    const { t } = useTranslation(['marketing', 'common'])

    const categoriesRaw = t('faq.categories', { returnObjects: true })
    const categories = (typeof categoriesRaw === 'object' && !Array.isArray(categoriesRaw) ? categoriesRaw : {}) as Record<string, string>
    const itemsRaw = t('faq.items', { returnObjects: true })
    const items = (Array.isArray(itemsRaw) ? itemsRaw : []) as FAQItem[]

    const groupedItems = Object.keys(categories).reduce((acc, catKey) => {
        acc[catKey] = items.filter(item => item.category === catKey)
        return acc
    }, {} as Record<string, FAQItem[]>)

    return (
        <div className="min-h-screen bg-background">
            <div className="container mx-auto px-4 py-8 pt-20">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-12">
                        <h1 className="text-4xl font-bold text-foreground mb-4">{t('faq.title')}</h1>
                        <p className="text-lg text-muted-foreground">
                            {t('faq.description')}
                        </p>
                    </div>

                    <div className="space-y-16 pb-16">
                        {Object.entries(categories).map(([catKey, catName]) => (
                            <section key={catKey}>
                                <h2 className="text-2xl font-bold mb-8 pb-2 border-b">{catName as string}</h2>
                                <div className="space-y-8">
                                    {groupedItems[catKey].map((item) => (
                                        <div key={item.id}>
                                            <h3 className="text-lg font-semibold text-foreground mb-2">{item.question}</h3>
                                            <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>

                    {/* CTA Section */}
                    <div className="bg-background">
                        <div className="container mx-auto px-4 pb-16">
                            <div className="max-w-4xl mx-auto">
                                <div className="rounded-xl border bg-primary/5 p-8 md:p-12 text-center">
                                    <Phone className="w-10 h-10 text-primary mx-auto mb-4" />
                                    <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                                        {t('blog.ctaTitle')}
                                    </h2>
                                    <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
                                        {t('blog.ctaDescription')}
                                    </p>
                                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                                        <Button size="lg" asChild>
                                            <Link to="/dialer">{t('navigation.getStarted')}</Link>
                                        </Button>
                                        <Button size="lg" variant="outline" asChild>
                                            <Link to="/pricing">{t('navigation.pricing')}</Link>
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
